import type { MeResponse, StatusResponse } from '@xchange/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api, RequestError } from './api.js';
import { domConditions, justUpdated, reloadForUpdate, safeToReload } from './update.js';
import { detectPlatform, type Platform } from './platform/index.js';
import { setHaptic, toast } from './toast.js';
import { ComingSoon } from './screens/ComingSoon.js';
import { SelfCustody } from './screens/SelfCustody.js';
import { WebShell } from './screens/WebShell.js';
import { WebTrade } from './screens/WebTrade.js';
import { needsConfirmation } from './wallet.js';
import { setTrackingPlatform, track, trackError } from './track.js';
import { WebSignIn } from './screens/WebSignIn.js';
import { Loading } from './screens/Loading.js';
import { Terms } from './screens/Terms.js';

type Screen = 'trade' | 'terms';

/**
 * Boot: find the host, sign in, read status, then show the wallet.
 * Every waiting state says what is happening in words.
 */
export function App() {
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>('trade');
  // A browser with no open session: the wallet signs in from its own screen.
  const [needsSignIn, setNeedsSignIn] = useState(false);
  // A wallet just made in the browser hands its words over to be shown and typed back.
  const [freshPhrase, setFreshPhrase] = useState<string | null>(null);
  // Set once a wallet made in the browser has had its words typed back.
  const [walletReady, setWalletReady] = useState(false);
  /** The release the page was loaded from. A change means a deploy happened. */
  const bootVersion = useRef<string | null>(null);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const gone = (): boolean => controller.signal.aborted;
    const boot = async (): Promise<void> => {
      const p = await detectPlatform();
      if (gone()) return;
      setPlatform(p);
      setTrackingPlatform(p.kind === 'web' ? 'web' : 'telegram');
      track('app_open');
      setHaptic(() => {
        p.tick();
      });
      // The API restarts for a few seconds at every deploy, and a phone's
      // network drops and returns. Neither is a reason to show an error:
      // keep trying quietly for half a minute before giving up.
      const transient = (e: unknown): boolean =>
        !(e instanceof RequestError) || e.status >= 500 || e.status === 0;
      const patiently = async <T,>(call: () => Promise<T>): Promise<T> => {
        const until = Date.now() + 30_000;
        for (;;) {
          try {
            return await call();
          } catch (e) {
            if (gone() || !transient(e) || Date.now() > until) throw e;
            await new Promise((r) => setTimeout(r, 1500));
          }
        }
      };
      const s = await patiently(() => api.status());
      if (gone()) return;
      setStatus(s);
      bootVersion.current = s.version;
      try {
        const m = await patiently(() => api.signIn(p));
        if (gone()) return;
        setMe(m);
        // Straight in with an open session: a returning device, not a lost newcomer.
        if (p.kind === 'web') track('start_screen', { is: 'session' });
      } catch (e) {
        // No session in a plain browser is the normal start, not an error.
        if (p.credential().kind === 'wallet' && e instanceof RequestError && e.status === 401)
          setNeedsSignIn(true);
        else throw e;
      }
      p.ready();
      if (justUpdated()) toast.ok('xchange has been updated to the latest version.');
    };
    boot().catch((e: unknown) => {
      if (gone()) return;
      trackError('boot');
      setError(e instanceof Error ? e.message : 'Could not start.');
    });
    return () => {
      controller.abort();
    };
  }, []);

  // Watch for a new release, and take it when taking it costs nothing.
  useEffect(() => {
    const check = (): void => {
      if (document.visibilityState !== 'visible') return;
      void api
        .status()
        .then((s) => {
          if (bootVersion.current !== null && s.version !== bootVersion.current) {
            setUpdateReady(true);
          }
        })
        .catch(() => {
          /* offline; try again next tick */
        });
    };
    const t = window.setInterval(check, 15_000);
    document.addEventListener('visibilitychange', check);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', check);
    };
  }, []);

  useEffect(() => {
    if (!updateReady) return;
    const since = Date.now();
    // Nothing at stake: reload now. After a minute, a half-typed amount no
    // longer holds the update back; a PIN pad or a trade in flight always does.
    const tryReload = (): void => {
      const c = domConditions();
      const patient = Date.now() - since < 60_000;
      if (safeToReload(patient ? c : { ...c, hasTypedText: false, inputFocused: false }))
        reloadForUpdate();
    };
    // Coming back to the app is the best moment of all.
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      // Same patience as the timer: a half-typed amount, or an address just
      // pasted in, is kept for the first minute rather than lost to a reload.
      const c = domConditions();
      const patient = Date.now() - since < 60_000;
      if (safeToReload(patient ? c : { ...c, hasTypedText: false, inputFocused: false }))
        reloadForUpdate();
    };
    tryReload();
    const t = window.setInterval(tryReload, 3000);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [updateReady]);

  // In a browser, every screen sits inside the site: top bar, market panel,
  // footer. Inside Telegram the screen is the whole window, as before.
  const wrap = (node: ReactNode, wide = false): ReactNode =>
    platform?.kind === 'web' ? (
      <WebShell
        me={me}
        status={status}
        wide={wide}
        onHome={() => {
          setScreen('trade');
        }}
        onSignOut={() => {
          void api
            .logout()
            .catch(() => undefined)
            .then(() => {
              window.location.reload();
            });
        }}
      >
        {node}
      </WebShell>
    ) : (
      node
    );

  if (error !== null && platform?.kind === 'web') {
    // A browser: say what happened and offer another go; nothing about Telegram.
    return wrap(
      <div className="screen center">
        <h2 className="title">Could not reach xchange</h2>
        <p className="hint">{error}</p>
        <button
          className="primary"
          onClick={() => {
            window.location.reload();
          }}
        >
          Try again
        </button>
      </div>,
    );
  }
  if (error !== null) {
    return wrap(
      <div className="screen center">
        <h1>Open xchange in Telegram</h1>
        {error.includes('initData') ? (
          <>
            <p className="hint">
              This page is the app inside Telegram, where Telegram signs you in. In a plain browser
              there is nobody to sign in.
            </p>
            <a
              className="primary"
              href="https://t.me/xchangehq_bot"
              style={{ display: 'block', textAlign: 'center' }}
            >
              Open @xchangehq_bot
            </a>
          </>
        ) : (
          <>
            <p className="hint">{error}</p>
            <p className="hint">Close this and open it again from the bot.</p>
          </>
        )}
      </div>,
    );
  }
  if (platform !== null && needsSignIn && me === null) {
    return wrap(
      <WebSignIn
        platform={platform}
        onSignedIn={(m, phrase) => {
          setFreshPhrase(phrase);
          setMe(m);
          setNeedsSignIn(false);
        }}
      />,
      // The welcome uses the width for the book beside it; the PIN screens keep their own column.
      true,
    );
  }
  if (platform === null || me === null || status === null) {
    return wrap(<Loading text="Opening your account." />);
  }
  if (status.comingSoon && !me.isAdmin) {
    return <ComingSoon />;
  }
  if (screen === 'terms') {
    return wrap(
      <Terms
        onBack={() => {
          setScreen('trade');
        }}
      />,
    );
  }
  // In a browser, trading has the whole screen. The phone-sized screens stay
  // for the moments that are the same everywhere: the words of a new wallet,
  // typed back, before anything else.
  if (
    platform.credential().kind === 'wallet' &&
    (freshPhrase === null || walletReady) &&
    !needsConfirmation()
  ) {
    return wrap(<WebTrade platform={platform} me={me} onMe={setMe} />, true);
  }
  return wrap(
    <SelfCustody
      platform={platform}
      me={me}
      freshPhrase={freshPhrase}
      onMe={setMe}
      onReady={() => {
        setFreshPhrase(null);
        setWalletReady(true);
      }}
      onTerms={() => {
        setScreen('terms');
      }}
    />,
  );
}
